import { useState, useMemo, useEffect } from 'react';
import { MasterCategoryConst, type MasterCategoryType } from '../types/masterSearchTypes';
import { useMasterReferenceQuery } from './useMasterReferenceGetItemListQuery';
import { MASTER_REFERENCE_CONFIG } from '../data/masterReferenceConfig';

/**
 * マスタ検索ダイアログで使用する初期値の型定義
 */
export interface MasterSearchInitialValues {
  code?: string | number | null;
  name?: string;
  sellerCode?: string | number | null;
  sellerName?: string;
}

/**
 * マスタ検索のロジックを管理するカスタムフック
 * データの取得、フィルタリング、表示カラムの制御、モーダル開閉時の状態管理を一括して行う
 */
export const useMasterSearch = (
  category: MasterCategoryType,
  initialValues: MasterSearchInitialValues,
  isOpen: boolean
) => {
  // --- 1. 検索フィルタ・選択状態の管理 ---
  const [filters, setFilters] = useState({ code: '', name: '' });           // 基本フィルタ（コード、名称）
  const [sellerFilter, setSellerFilter] = useState({ code: '', name: '' }); // 売手フィルタ（Farmer検索時のみ使用）
  const [selectedRow, setSelectedRow] = useState<any | null>(null);         // テーブルで選択された行

  // --- 2. 外部データの取得 ---
  // 指定されたカテゴリのマスタデータを取得
  const { data: masterData, isLoading } = useMasterReferenceQuery(isOpen, category);
  
  // カテゴリが「生産者(FARMER)」の場合のみ、結合表示用の「売手(SELLER)」マスタも追加で取得
  const { data: sellerMasterData } = useMasterReferenceQuery(
    isOpen && category === MasterCategoryConst.FARMER,
    MasterCategoryConst.SELLER
  );

  // マスタごとの静的設定（カラム定義やラベル）を取得
  const config = MASTER_REFERENCE_CONFIG[category];
  const isFarmer = category === MasterCategoryConst.FARMER;

  // --- 3. ライフサイクル管理（ダイアログ開閉時の初期化） ---
  useEffect(() => {
    if (isOpen) {
      // ダイアログ表示時：背面（body）のスクロールを抑制
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';

      // 呼び出し元からの初期値を検索入力欄に反映
      setFilters({
        code: initialValues?.code != null ? String(initialValues.code) : '',
        name: String(initialValues?.name || '')
      });
      setSellerFilter({
        code: initialValues?.sellerCode != null ? String(initialValues.sellerCode) : '',
        name: String(initialValues?.sellerName || '')
      });

      // 選択状態をリセット
      setSelectedRow(null);

      // クリーンアップ処理：ダイアログを閉じる際にスクロール抑制を解除
      return () => { document.body.style.overflow = originalStyle; };
    }
  }, [isOpen, initialValues]);

  // --- 4. 表示カラムの動的生成 ---
  // 設定ファイルに基づき、テーブルに表示するカラムを計算
  const displayColumns = useMemo(() => {
    if (!config || !config.columns) return [];
    
    // 日付やチェックボックスなど、検索ダイアログには不要なカラムを除外
    let cols = config.columns.filter(col => col.type !== 'date' && col.type !== 'checkbox');
    
    // 生産者検索の場合、ユーザの利便性のため「売手名」カラムを2番目に挿入
    if (isFarmer) {
      const sellerNameCol = { header: '売手名', accessor: 'sellerName' };
      const newCols = [...cols];
      newCols.splice(1, 0, sellerNameCol);
      return newCols;
    }
    return cols;
  }, [config, isFarmer]);

  // --- 5. データの加工（マージ・フィルタリング） ---
  const processedData = useMemo(() => {
    if (!masterData?.masters) return [];

    // ① 有効なデータのみに絞り込み
    let list = masterData.masters.filter((m: any) => !m.isDeleted);
    
    // ② データの結合（Farmerの場合、Seller情報を紐付け）
    // 外部キー(sellerCode)を元に、sellerMasterDataから名称を引いてくる
    if (isFarmer && sellerMasterData?.masters) {
      list = list.map((farmer: any) => {
        const seller = sellerMasterData.masters.find((s: any) =>
          String(s.sellerCode).trim() === String(farmer.sellerCode).trim()
        );
        return { ...farmer, sellerName: seller?.sellerName || '' };
      });
    }

    // ③ 入力されたフィルタ条件で絞り込み
    return list.filter((item: any) => {
      if (!config) return true;

      // カテゴリによって検索対象とするプロパティ(アクセサ)を特定
      const codeKey = isFarmer ? config.columns[1]?.accessor : config.columns[0]?.accessor;
      const nameKey = isFarmer ? config.columns[2]?.accessor : config.columns[1]?.accessor;
      
      // 文字列の部分一致（インクルード）検索
      // マスター側の値（数値）を String() でキャストしてから比較
      const itemCodeStr = item[codeKey] != null ? String(item[codeKey]) : '';
      const itemNameStr = item[nameKey] != null ? String(item[nameKey]) : '';

      const matchCode = !filters.code || itemCodeStr.includes(filters.code);
      const matchName = !filters.name || itemNameStr.includes(filters.name);
      
      // Farmer特有の売手フィルタリング
      if (isFarmer) {
        const itemSellerCodeStr = item.sellerCode != null ? String(item.sellerCode) : '';
        const itemSellerNameStr = item.sellerName != null ? String(item.sellerName) : '';

        const matchSellerCode = !sellerFilter.code || itemSellerCodeStr.includes(sellerFilter.code);
        const matchSellerName = !sellerFilter.name || itemSellerNameStr.includes(sellerFilter.name);
        return matchCode && matchName && matchSellerCode && matchSellerName;
      }

      return matchCode && matchName;
    });
  }, [masterData, sellerMasterData, category, filters, sellerFilter, config, isFarmer]);

  // コンポーネントで必要な状態と関数を公開
  return {
    filters, setFilters,
    sellerFilter, setSellerFilter,
    selectedRow, setSelectedRow,
    processedData,
    displayColumns,
    isLoading,
    config,
    isFarmer
  };
};